import { useState, useRef } from 'react';

export default function LineChart({ points, color = "#4f8fff", height = 90, unit = "" }) {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  if (!points || points.length < 2) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 12, color: "var(--text-3)" }}>Log more sessions to see chart</span>
      </div>
    );
  }
  const values = points.map(p => p.y);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const range = maxV - minV || 1;
  const W = 300, H = height, PX = 8, PY = 14;
  const toX = i => PX + (i / (points.length - 1)) * (W - PX * 2);
  const toY = v => H - PY - ((v - minV) / range) * (H - PY * 2);
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${toX(i)} ${toY(p.y)}`).join(" ");
  const areaD = pathD + ` L ${toX(points.length - 1)} ${H} L ${toX(0)} ${H} Z`;
  const gradId = `g${color.replace(/[^a-z0-9]/gi, "")}`;

  function getClosest(clientX) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const relX = (clientX - rect.left) / rect.width * W;
    let closest = 0, minDist = Infinity;
    points.forEach((p, i) => {
      const dist = Math.abs(toX(i) - relX);
      if (dist < minDist) { minDist = dist; closest = i; }
    });
    return { i: closest, ...points[closest] };
  }

  return (
    <div style={{ position: "relative" }}>
      {tooltip && (
        <div style={{
          position: "absolute", top: 0,
          left: `${(toX(tooltip.i) / W) * 100}%`,
          transform: "translateX(-50%)",
          background: "var(--bg-3)", border: `1px solid ${color}44`,
          borderRadius: 8, padding: "4px 8px", pointerEvents: "none", zIndex: 10,
          whiteSpace: "nowrap",
        }}>
          <div style={{ fontSize: 12, fontWeight: 800, color }}>{tooltip.y}{unit}</div>
          <div style={{ fontSize: 10, color: "var(--text-3)" }}>{tooltip.label}</div>
        </div>
      )}
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height, display: "block" }}
        onMouseDown={e => setTooltip(getClosest(e.clientX))}
        onMouseMove={e => { if (e.buttons === 1) setTooltip(getClosest(e.clientX)); }}
        onMouseUp={() => setTooltip(null)}
        onMouseLeave={() => setTooltip(null)}
        onTouchStart={e => setTooltip(getClosest(e.touches[0].clientX))}
        onTouchMove={e => setTooltip(getClosest(e.touches[0].clientX))}
        onTouchEnd={() => setTooltip(null)}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={areaD} fill={`url(#${gradId})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle key={i} cx={toX(i)} cy={toY(p.y)} r={tooltip?.i === i ? 5.5 : 3.5}
            fill={tooltip?.i === i ? "white" : color}
            stroke={tooltip?.i === i ? color : "none"} strokeWidth="2" />
        ))}
        {tooltip && (
          <line x1={toX(tooltip.i)} y1={0} x2={toX(tooltip.i)} y2={H}
            stroke={color} strokeWidth="1" strokeDasharray="3,3" opacity="0.4" />
        )}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 8px 0" }}>
        <span style={{ fontSize: 10, color: "var(--text-3)" }}>{points[0]?.label}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color }}>
          {points[points.length - 1]?.y}{unit}
          {points.length >= 2 && (() => {
            const diff = points[points.length-1].y - points[0].y;
            if (diff === 0) return null;
            return <span style={{ fontSize: 10, marginLeft: 4, color: diff > 0 ? "var(--green)" : "var(--red)" }}>{diff > 0 ? "+" : ""}{diff}{unit}</span>;
          })()}
        </span>
        <span style={{ fontSize: 10, color: "var(--text-3)" }}>{points[points.length - 1]?.label}</span>
      </div>
    </div>
  );
}
